export class LayerNode {
    constructor(layer, parentNodes) {
        this.children = [];
        this.layerGraph = {};
        this.hasBeenUpdated = false;
        this.isActive = false;
        this.layer = layer;
        this.parentNodes = parentNodes;
        this.inactiveParents = new Set(parentNodes);
        for (const parent of this.parentNodes) {
            parent.children.push(this);
        }
    }
    setLayerGraph(layerGraph) {
        this.layerGraph = layerGraph;
        this.layer._setLayerGraph(layerGraph);
    }
    onParentActivated(parent) {
        this.inactiveParents.delete(parent);
    }
    onParentDeactivated(parent) {
        this.inactiveParents.add(parent);
    }
    canActivate() {
        let inactiveParentsString = '';
        this.inactiveParents.forEach((parent) => inactiveParentsString += ' - ' + parent.layer.constructor.name);
        //Debug.log(`$${this.layer.constructor.name} canActivate(). Inactive parents: ${inactiveParentsString}, dependsOn: ${[...(this.layer as any).dependsOn]}`);
        return this.layer.canActivate() && this.inactiveParents.size === 0;
    }
    activate() {
        //Debug.log(`${this.layer.constructor.name} activating`);
        this.isActive = true;
        this.layer.activate();
        for (const child of this.children) {
            child.onParentActivated(this);
        }
        for (const child of this.children) {
            if (!child.isActive && child.canActivate()) {
                child.activate();
            }
        }
    }
    deactivate() {
        // Debug.log(`${this.layer.constructor.name} closing`);
        this.isActive = false;
        this.layer.close();
        for (const child of this.children) {
            child.onParentDeactivated(this);
        }
        for (const child of this.children) {
            if (child.isActive && !child.canActivate()) {
                child.deactivate();
            }
        }
    }
}
export class LayerGraph {
    constructor(roots) {
        this.sortedLayers = [];
        this.sortedLayers = this.sortLayers(roots);
        for (const layer of this.sortedLayers) {
            layer.setLayerGraph(this);
        }
        let sortedLayersString = '';
        this.sortedLayers.forEach((layer) => sortedLayersString += ' - ' + layer.layer.constructor.name);
        // console.log(`SORTED LAYERS: ${sortedLayersString}`);
        for (const layer of this.sortedLayers) {
            if (!layer.isActive && layer.canActivate()) {
                layer.activate();
            }
        }
        this.context = Object.assign({}, globalThis.Debug.getDebugContext());
    }
    sortLayers(roots) {
        const nodeList = [];
        const visited = new Set();
        const cycleSet = new Set();
        const cycleList = [];
        for (const root of roots) {
            this.sortLayersHelper(root, nodeList, visited, cycleSet, cycleList);
        }
        nodeList.reverse();
        return nodeList;
    }
    sortLayersHelper(current, nodeList, visited, cycleSet, cycleList) {
        visited.add(current);
        cycleSet.add(current);
        cycleList.push(current);
        for (const child of current.children) {
            if (cycleSet.has(child)) {
                this.throwOnCycle(cycleList, child);
            }
            if (!visited.has(child)) {
                this.sortLayersHelper(child, nodeList, visited, cycleSet, cycleList);
            }
        }
        cycleSet.delete(current);
        cycleList.pop();
        nodeList.push(current);
    }
    throwOnCycle(cycleList, child) {
        const idx = cycleList.findIndex((elem) => elem === child);
        let cycleString = '';
        for (let i = idx; i < cycleList.length; i++) {
            cycleString += `${cycleList[i].layer.constructor.name} -> `;
        }
        cycleString += child.layer.constructor.name;
        throw new Error(`Cycle detected in layer nodes: ${cycleString}`);
    }
    sendUpdate(triggeringRoot, inEvent) {
        globalThis.Debug.pushDebugContext(this.context);
        const startIdx = this.sortedLayers.findIndex((elem) => elem.layer === triggeringRoot);
        const dataMap = new Map();
        const key = inEvent.key;
        const type = inEvent.type;
        const applyDependencyChange = (layer) => {
            if (type === 'Created') {
                this.publishDependency(layer, key);
            }
            if (type === 'Deleted') {
                this.unpublishDependency(layer, key);
            }
        };
        //const dependencyStateMap: Record<string, 'Created' | 'Deleted'> = {};
        for (let i = 0; i < this.sortedLayers.length; i++) {
            const layerNode = this.sortedLayers[i];
            const layer = layerNode.layer;
            if (i < startIdx) {
                dataMap.set(layer, null);
                continue;
            }
            applyDependencyChange(layerNode);
            if (i === startIdx) {
                dataMap.set(layer, inEvent);
                continue;
            }
            if (!layerNode.canActivate()) {
                continue;
            }
            const updateInput = this.getLayerUpdateInput(layerNode, dataMap);
            if (updateInput === null) {
                continue;
            }
            const updateOutput = layer.update(updateInput);
            layerNode.hasBeenUpdated = true;
            dataMap.set(layer, updateOutput);
        }
        globalThis.Debug.popDebugContext(this.context);
    }
    getLayerUpdateInput(layerNode, dataMap) {
        let inData = null;
        for (const parent of layerNode.parentNodes) {
            const parentData = dataMap.get(parent.layer);
            if (parentData === null || parentData === undefined) {
                continue;
            }
            // if (parentData === undefined) {
            //     throw new Error(`Parent layer data wasn't in the map during an update!`);
            // }
            if (inData !== null) {
                throw new Error(`Layer receiving updates from multiple parents at once! Layers may only receive updates from a single parent at a time.\nUpdate1: ${inData} Update2: ${parentData}`);
            }
            inData = parentData;
        }
        // if (inData === null) {
        //     throw new Error(`Layer didn't receive any data from parents during an update!`);
        // }
        return inData;
    }
    // private handleDependencyChangesFromEvent(layerNode: LayerNode, inEvent: DependencyEvent<any> | null, /*dependencyStateMap: Record<string, 'Created' | 'Deleted'>*/) {
    //     if (inEvent === null) {
    //         return;
    //     }
    //     console.log(`HandleDependencyChangesFromEvent. Layer: ${layerNode.layer.constructor.name}, eventType: ${inEvent.type}, eventKey: ${inEvent.key}`);
    //     if (inEvent.type === 'Created') {
    //         // if (dependencyStateMap[inEvent.key] === 'Created') {
    //         //     return;
    //         // }
    //         // dependencyStateMap[inEvent.key] = 'Created';
    //         this.publishDependency(layerNode, inEvent.key);
    //     }
    //     if (inEvent.type === 'Deleted') {
    //         // if (dependencyStateMap[inEvent.key] === 'Deleted') {
    //         //     return;
    //         // }
    //         // dependencyStateMap[inEvent.key] = 'Deleted';
    //         this.unpublishDependency(layerNode, inEvent.key);
    //     }
    // }
    publishDependency(layerNode, dep) {
        const layer = layerNode.layer;
        layer.onDependencyAvailable(dep);
        if (!layerNode.isActive && layerNode.canActivate()) {
            layerNode.activate();
        }
    }
    unpublishDependency(layerNode, dep) {
        const layer = layerNode.layer;
        layer.onDependencyUnavailable(dep);
        if (layerNode.isActive && !layerNode.canActivate()) {
            layerNode.deactivate();
        }
    }
}
//# sourceMappingURL=LayerGraph.js.map